import {
  Box,
  Circle,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../helpers/format-price";
import { Button } from "../../components/chackra-ui-button";

type Props = {
  hostedInvoiceUrl?: string | null;
  amountRemaining: number;
};

export const PendingPayment = (props: Props) => {
  const { t } = useTranslation();
  const [showRefresh, setShowRefresh] = useState(false);
  const hostedInvoiceUrl = props.hostedInvoiceUrl;

  return (
    <VStack
      w="full"
      flex={1}
      justify="center"
      align="center"
      py={4}
    >
      <VStack w="full" maxW="400px" spacing={0}>
        {/* Icon */}
        <Circle size="56px" bg="red.100" mb={4}>
          <Icon boxSize={6} color="red.500">
            <ExclamationTriangleIcon />
          </Icon>
        </Circle>

        {/* Title */}
        <Text
          fontSize="20px"
          fontWeight={700}
          fontFamily="'Barlow Condensed', sans-serif"
          color="#13192E"
          textAlign="center"
          mb={2}
        >
          {t("pendingPayment.title")}
        </Text>

        {/* Description */}
        <Text
          fontSize="14px"
          color="slate.500"
          textAlign="center"
          lineHeight="1.5"
          px={2}
          mb={6}
        >
          {hostedInvoiceUrl
            ? t("pendingPayment.continuePayOnline")
            : t("pendingPayment.continuePayReception")}
        </Text>

        {/* Amount row */}
        <Box
          w="full"
          bg="slate.50"
          borderRadius="12px"
          px={4}
          py={3}
          mb={4}
        >
          <HStack justify="space-between" align="center">
            <Text fontSize="14px" color="slate.500">
              {t("pendingPayment.amountDue")}
            </Text>
            <Text
              fontFamily="'Barlow Semi Condensed', sans-serif"
              fontSize="18px"
              fontWeight={700}
              color="#13192E"
            >
              {formatPrice({ t, amount: props.amountRemaining })}
            </Text>
          </HStack>
        </Box>

        {/* Recurring notice */}
        <Box
          w="full"
          bg="slate.50"
          borderWidth="1px"
          borderColor="slate.200"
          borderRadius="10px"
          p={3}
          mb={5}
        >
          <Text fontSize="13px" color="slate.600" textAlign="center" lineHeight="1.4">
            {t("pendingPayment.recurringNotice")}
          </Text>
        </Box>

        {/* Pay button */}
        {hostedInvoiceUrl && (
          <Button
            href={hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            as="a"
            onClick={() => setShowRefresh(true)}
            variant="senseiFilled"
            w="full"
            mb={3}
            rightIcon={
              <Icon boxSize={4}>
                <ArrowTopRightOnSquareIcon />
              </Icon>
            }
          >
            {t("pendingPayment.proceedToPayment")}
          </Button>
        )}

        {/* Refresh after payment */}
        {showRefresh && (
          <Button
            variant="senseiOutlined"
            w="full"
            onClick={() => window.location.reload()}
          >
            {t("pendingPayment.refresh")}
          </Button>
        )}

        <Text fontSize="13px" color="slate.400" textAlign="center" mt={2}>
          {t("pendingPayment.secureRedirect")}
        </Text>
      </VStack>
    </VStack>
  );
};
